// node_modules
import React, { useState, useEffect, useContext, useRef } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { useSelector, useDispatch } from "react-redux";

// redux
import { useGetDiningOptionListQuery } from "../../../redux/services/diningOption";
import { useGetStationOptionsListQuery } from "../../../redux/services/foodStationOptions";
import { useCreateOrderMutation } from "../../../redux/services/order";
import { useGetOrganizationNotificationOptionsCustomerQuery } from "../../../redux/services/organization";
import { useGetCustomerMeQuery, customerApi } from "../../../redux/services/customer";
import {
  useNotifyStartMutation,
  useNotifyVerifyMutation,
  useNotifyResendMutation,
} from "../../../redux/services/telegram";
import { setLoading } from "../../../redux/slices/app.slice";
import {
  getEffectiveNotificationChannels,
  getEffectiveAllergies,
} from "../../../utils/customerDefaults";

// primereact components
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { MultiSelect } from "primereact/multiselect";
import { Checkbox } from "primereact/checkbox";

// components
import Confetti from "../../../components/Confetti";
import { MobileSelectModal } from "./components/MobileSelectModal";
import { MenuItemInfoIcon } from "./components/MenuItemInfoIcon";

// redux

// context
import { ToastContext } from "../../../App";

// utils
import getSubdomain from "../../../utils/getSubdomain";
import {
  getSubscription,
  requestPermission,
  subscribe,
  unsubscribe,
} from "../../../utils/notification";
import { formatOrderRestriction } from "../../../utils/convertStringToMinutes";
import { isIOS } from "../../../utils/isIOS";
import { isRunningAsPWA } from "../../../utils/isRunningAsPWA";
import LinkView from "../../../components/LinkView/LinkView";
import { useMobile } from "../../../hooks/useMobile";
import useFilteredOptionGroups from "../../../hooks/useFilteredOptionGroups";
import {
  allergiesOptions,
  allergiesImageMapper,
  allergiesNameMapper,
} from "./constants/allergies";

// styles
import * as styles from "./OrderPage.module.css";

// assets
import NoImg from "../../../assets/no_img.svg";
import { getImageUrl } from "../../../utils/getImageUrl";
import { TelegramNotificationField } from "./components/TelegramNotificationField/TelegramNotificationField";
import { classNames } from "primereact/utils";

const OrderPage = ({ sendViaWS, stationChanged, setStationChanged }) => {
  const { id: stationId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const preselectedSubStationId = location?.state?.subStationId;

  const installPrompt = useRef(null);
  const { isMobile } = useMobile(430);
  const showToast = useContext(ToastContext);

  const [showMobilePushInfo, setShowMobilePushInfo] = useState(false);
  const [hasInstalledPwa, setHasInstalledPwa] = useState(false);
  const [showAllergenItems, setShowAllergenItems] = useState(false);

  const vapidKey = useSelector((state) => state.app.vapidKey);

  const {
    control,
    handleSubmit,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      allergies: [],
    },
  });

  const notificationType = watch("notification");

  const openedStations = useSelector((state) => state.stations.openedStations);

  const {
    data: options,
    isSuccess: isGetDiningOptionsSuccess,
    isError: isGetDiningOptionsError,
  } = useGetDiningOptionListQuery({ id: stationId, subDomain: getSubdomain() });

  const {
    data: notifications,
    isSuccess: isGetNotificationsSuccess,
    isError: isGetNotificationsError,
  } = useGetOrganizationNotificationOptionsCustomerQuery(getSubdomain());

  const { data: customerProfile } = useGetCustomerMeQuery(undefined, {
    skip: !localStorage.getItem("access_token"),
  });

  const savedChannels = customerProfile?.notification_channels;
  const effectiveCustomerChannels = Array.isArray(savedChannels)
    ? savedChannels
    : null;
  const filteredNotifications =
    notifications && effectiveCustomerChannels
      ? notifications.filter((n) => effectiveCustomerChannels.includes(n.title))
      : notifications;

  const singleNotificationType =
    filteredNotifications?.length === 1 ? filteredNotifications[0].title : null;

  const effectiveNotificationType = customerProfile
    ? notificationType || singleNotificationType
    : null;

  const [notifyStart, { isLoading: isNotifyStarting }] =
    useNotifyStartMutation();
  const [notifyVerify, { isLoading: isNotifyVerifying }] =
    useNotifyVerifyMutation();
  const [notifyResend, { isLoading: isNotifyResending }] =
    useNotifyResendMutation();

  const [notifyStartToken, setNotifyStartToken] = useState(null);
  const [notifyOtp, setNotifyOtp] = useState("");
  const [notifyError, setNotifyError] = useState(null);
  const [telegramChatId, setTelegramChatId] = useState(null);
  const [telegramVerified, setTelegramVerified] = useState(false);

  const currentStation = openedStations?.find(
    (station) => +station.id === +stationId,
  );

  const selectedSubStation = currentStation?.sub_stations?.find(
    (s) => +s.id === +preselectedSubStationId,
  );

  const headerImageUrl = selectedSubStation?.image_name
    ? getImageUrl(selectedSubStation.image_name)
    : currentStation?.image_name
      ? getImageUrl(currentStation.image_name)
      : "";

  const {
    data: stationOptions,
    isSuccess: isGetStationOptionsSuccess,
    isError: isGetStationOptionsError,
  } = useGetStationOptionsListQuery({
    id: stationId,
    subDomain: getSubdomain(),
  });
  const formValues = watch();
  const { selectedByGroup, filteredGroupIds, filteredGroups, allowedItemIds } =
    useFilteredOptionGroups(stationOptions, formValues, showAllergenItems);

  useEffect(() => {
    if (!allowedItemIds) return;

    const values = getValues();
    const keys = Object.keys(values).filter((k) =>
      k.startsWith("menu_option-"),
    );
    let changed = false;

    for (const key of keys) {
      const val = values[key];
      if (val === undefined || val === null) continue;

      if (Array.isArray(val)) {
        const newVal = val.filter((v) => allowedItemIds.has(String(v)));
        if (newVal.length !== val.length) {
          setValue(key, newVal, { shouldValidate: true });
          changed = true;
        }
      } else {
        if (!allowedItemIds.has(String(val))) {
          setValue(key, undefined, { shouldValidate: true });
          changed = true;
        }
      }
    }

    if (changed) {
      showToast(
        "info",
        "Selections updated",
        "Some selections were removed because they contain selected allergen(s)",
      );
    }
  }, [JSON.stringify(formValues.allergies || []), showAllergenItems]);

  useEffect(() => {
    setShowAllergenItems(false);
  }, [JSON.stringify(formValues.allergies || [])]);

  const [allergiesPrefilled, setAllergiesPrefilled] = useState(false);
  useEffect(() => {
    if (allergiesPrefilled) return;
    if (!customerProfile) return;
    setValue("allergies", getEffectiveAllergies(customerProfile), {
      shouldValidate: true,
    });
    setAllergiesPrefilled(true);
  }, [customerProfile, allergiesPrefilled, setValue]);

  useEffect(() => {
    if (options?.length === 1 && !getValues("dining_option")) {
      setValue("dining_option", options[0].id, { shouldValidate: true });
    }
  }, [options]);

  const [namePrefilled, setNamePrefilled] = useState(false);
  useEffect(() => {
    if (namePrefilled) return;
    if (!customerProfile) return;
    if (customerProfile.first_name) {
      setValue("name", customerProfile.first_name, { shouldValidate: true });
    }
    if (customerProfile.last_name) {
      setValue("surname", customerProfile.last_name, { shouldValidate: true });
    }
    setNamePrefilled(true);
  }, [customerProfile, namePrefilled, setValue]);

  const [notifPrefilled, setNotifPrefilled] = useState(false);
  useEffect(() => {
    if (notifPrefilled) return;
    if (!filteredNotifications?.length) return;
    if (!customerProfile) return;
    if (filteredNotifications.length === 1) {
      setValue("notification", filteredNotifications[0].title, { shouldValidate: true });
      setNotifPrefilled(true);
      return;
    }
    const preferred = getEffectiveNotificationChannels(customerProfile).find(
      (ch) => filteredNotifications.some((n) => n.title === ch),
    );
    if (preferred) {
      setValue("notification", preferred, { shouldValidate: true });
    }
    setNotifPrefilled(true);
  }, [filteredNotifications, customerProfile, notifPrefilled, setValue]);

  // Keep the contact field in sync with the active channel: prefill it from the
  // saved profile, and clear the inactive one. Done in a single effect (no
  // setTimeout race) so the prefilled value isn't wiped on auto-selection.
  useEffect(() => {
    if (!customerProfile) return;

    if (effectiveNotificationType === "text") {
      setValue(
        "phone",
        customerProfile.phone
          ? customerProfile.phone.replace(/\D/g, "").slice(-10)
          : "",
      );
    } else {
      setValue("phone", "");
    }

    if (effectiveNotificationType === "email") {
      setValue("email", customerProfile.email || "");
    } else {
      setValue("email", "");
    }

    if (
      effectiveNotificationType === "telegram" &&
      customerProfile.telegram_chat_id
    ) {
      setTelegramChatId(customerProfile.telegram_chat_id);
      setTelegramVerified(true);
    }
  }, [effectiveNotificationType, customerProfile, setValue]);

  const [
    createOrder,
    {
      isSuccess: isCreateOrderSuccess,
      isError: isCreateOrderError,
      error: orderError,
      isLoading: isOrderLoading,
    },
  ] = useCreateOrderMutation();

  useEffect(() => {
    window.addEventListener("beforeinstallprompt", (event) => {
      event.preventDefault();
      installPrompt.current = event;
    });
    dispatch(setLoading(true));

    const checkInstalled = async () => {
      if ("getInstalledRelatedApps" in navigator) {
        const relatedApps = await navigator.getInstalledRelatedApps();

        const hasInstalledPWA = relatedApps.some(
          (app) => app.platform === "webapp",
        );

        setHasInstalledPwa(hasInstalledPWA);
      }
    };

    checkInstalled();
  }, []);

  useEffect(() => {
    if (isGetStationOptionsSuccess && isGetDiningOptionsSuccess) {
      dispatch(setLoading(false));
    }
  }, [isGetDiningOptionsSuccess, isGetStationOptionsSuccess]);

  useEffect(() => {
    if (stationChanged) {
      setValue("dining_option", undefined);

      for (const option of stationOptions || []) {
        setValue(`menu_option-${option.group.group_name}`, undefined);
      }

      setStationChanged(false);
    }
  }, [stationChanged]);

  useEffect(() => {
    const groups = filteredGroups || stationOptions || [];
    for (const rawOption of groups) {
      const visibleItems = (rawOption.items || []).filter(
        (item) => item.visible,
      );
      if (visibleItems.length === 1) {
        const key = `menu_option-${rawOption.group.group_name}`;
        if (rawOption.group.allow_multiple) {
          setValue(key, [visibleItems[0].id], { shouldValidate: true });
        } else {
          setValue(key, visibleItems[0].id, { shouldValidate: true });
        }
      }
    }
  }, [filteredGroups, stationOptions]);

  useEffect(() => {
    if (isCreateOrderSuccess) {
      dispatch(customerApi.util.invalidateTags([{ type: "CustomerProfile", id: "ME" }]));

      if (customerProfile?.id) {
        sendViaWS({
          type: "order-status",
          data: { customer_id: customerProfile.id },
        });
      }

      setTimeout(() => {
        navigate("/");
      }, 4000);
    }
  }, [isCreateOrderSuccess]);

  useEffect(() => {
    if (isCreateOrderError) {
      if (orderError.data.errors?.length) {
        orderError.data.errors.forEach((error) => {
          if (error.param.startsWith("/")) {
            showToast(
              "error",
              orderError.status,
              error.param + " " + error.message,
            );
          } else {
            showToast("error", orderError.status, error.message);
          }
        });
      } else {
        showToast("error", orderError.status, orderError.data.message);
      }
    }
  }, [isCreateOrderError]);

  useEffect(() => {
    if (isGetDiningOptionsError) {
      showToast("error", "Dining option", "Unable to load dining options");
    }
  }, [isGetDiningOptionsError]);

  useEffect(() => {
    if (isGetNotificationsError) {
      showToast("error", "Notification", "Unable to load notification options");
    }
  }, [isGetNotificationsError]);

  // Clear telegram linking state when telegram isn't the active channel. Keyed
  // on effectiveNotificationType (not the raw form value) so it stays in sync
  // with the prefill effect above — otherwise, on a soft remount where the
  // profile is already cached, this would wipe the just-prefilled "connected"
  // state before the form's notification value catches up (only a hard reload
  // would then show telegram as linked).
  useEffect(() => {
    if (effectiveNotificationType !== "telegram") {
      setNotifyStartToken(null);
      setNotifyOtp("");
      setNotifyError(null);
      setTelegramChatId(null);
      setTelegramVerified(false);
    }
  }, [effectiveNotificationType]);

  useEffect(() => {
    if (isGetStationOptionsError) {
      showToast("error", "Station option", "Unable to load station options");
    }
  }, [isGetStationOptionsError]);

  useEffect(() => {
    if (notificationType === "push") {
      if (
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent,
        )
      ) {
        if (isRunningAsPWA()) {
          requestPermission().then((a) => {
            if (Notification.permission !== "granted") {
              if (a === "granted") {
                if (!vapidKey) {
                  sendViaWS({
                    type: "get-vapid-key",
                  });
                }
              } else {
                showToast(
                  "error",
                  "Error!",
                  "Push notifications is disabled or app opened in Incognito Mode",
                );
                setValue("notification", "");
              }
            }
          });
        } else {
          setShowMobilePushInfo(true);
          setValue("notification", "");
        }
      } else {
        if ("Notification" in window && Notification.permission === "denied") {
          requestPermission().then((a) => {
            if (a === "denied") {
              showToast(
                "error",
                "Error!",
                "Push notifications is disabled or app opened in Incognito Mode",
              );
              setValue("notification", "");
            }
          });
        }
        if ("Notification" in window && Notification.permission !== "denied") {
          requestPermission().then((a) => {
            if (a === "granted") {
              if (!vapidKey) {
                sendViaWS({
                  type: "get-vapid-key",
                });
              }
            } else {
              showToast(
                "error",
                "Error!",
                "Push notifications is disabled or app opened in Incognito Mode",
              );
              setValue("notification", "");
            }
          });
        }
      }
    } else if (notificationType === "email" || notificationType === "text") {
      setShowMobilePushInfo(false);
      unsubscribe();
    }
  }, [vapidKey, notificationType]);

  const installPWA = async () => {
    try {
      await installPrompt.current.prompt();
    } catch (error) {
      showToast("error", "Error", "Can't install PWA");
    }
  };

  const onCreate = async (data) => {
    if (isOrderLoading) return;

    const payload = {
      customer_id: String(customerProfile?.id),
      subdomain: getSubdomain(),
      food_station_id: +stationId,
      dining_option_id: data.dining_option,
      name: data.name,
      surname: data.surname,
      special_note: data.notes,
      options_id: [],
      allergies: data.allergies,
      notification: {},
    };

    if (preselectedSubStationId) {
      payload.sub_station_id = +preselectedSubStationId;
    }

    if (data.notification === "push") {
      try {
        await subscribe(vapidKey);
        const subRes = await getSubscription();
        const sub = subRes.toJSON();
        payload.notification = {
          type: "push",
          auth: sub.keys.auth,
          p256dh: sub.keys.p256dh,
          endpoint: sub.endpoint,
        };
      } catch (e) {
        console.error("push failed:", e);
        showToast("error", "Error!", "Failed to enable push notifications. Please pick another option.");
        return;
      }
    }

    if (data.notification === "text") {
      payload.notification = {
        type: "text",
        phone: `+1${data.phone}`,
      };
    }

    if (data.notification === "email") {
      payload.notification = {
        type: "email",
        email: data.email,
      };
    }

    if (data.notification === "telegram") {
      payload.notification = {
        type: "telegram",
        telegram_chat_id: telegramChatId || null,
        start_token: notifyStartToken || null,
      };
    }

    const menuOptionKeys = Object.keys(data).filter((key) =>
      key.startsWith("menu_option-"),
    );

    menuOptionKeys.forEach((key) => {
      const value = data[key];

      if (Array.isArray(value)) {
        value.forEach((item) => {
          if (item != null) {
            payload.options_id.push(item);
          }
        });
      } else if (value != null) {
        payload.options_id.push(value);
      }
    });

    createOrder(payload);
    // Today's orders are refreshed via the WS `order-status` request fired on
    // create success (see effect above), so no optimistic local push is needed.
  };

  useEffect(() => {
    if (
      filteredNotifications &&
      notificationType &&
      !filteredNotifications.map((v) => v.title).includes(notificationType)
    ) {
      setValue("notification", "");
    }
  }, [notifications, customerProfile, notificationType]);

  const contactFieldsRenderer = () => {
    switch (effectiveNotificationType) {
      case "telegram":
        return (
          <TelegramNotificationField
            telegramChatId={telegramChatId}
            notifyVerify={notifyVerify}
            telegramVerified={telegramVerified}
            notifyStartToken={notifyStartToken}
            setNotifyStartToken={setNotifyStartToken}
            notifyOtp={notifyOtp}
            setNotifyOtp={setNotifyOtp}
            notifyError={notifyError}
            setNotifyError={setNotifyError}
            notifyStart={notifyStart}
            notifyResend={notifyResend}
            setTelegramVerified={setTelegramVerified}
            setTelegramChatId={setTelegramChatId}
            showToast={showToast}
            nickname={customerProfile?.nickname}
          />
        );
      case "email":
        return (
          <Controller
            name="email"
            control={control}
            defaultValue={customerProfile?.email ?? ""}
            rules={{
              required: "Enter your email",
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: "Email is invalid",
              },
            }}
            render={({ field, fieldState }) => (
              <div className="field">
                <label className={styles.label}>Email *</label>
                <div className={styles.inputWrapper}>
                  <InputText
                    className={`w-full ${styles.input} ${
                      fieldState.invalid ? "p-invalid" : ""
                    }`}
                    placeholder="Enter Your Email"
                    {...field}
                  />
                  {fieldState.error && (
                    <small className={`p-error `}>
                      {fieldState.error.message}
                    </small>
                  )}
                </div>
              </div>
            )}
          />
        );
      case "text":
        return (
          <Controller
            name="phone"
            control={control}
            defaultValue={(customerProfile?.phone ?? "").replace(/\D/g, "").slice(-10)}
            rules={{
              required: "Enter your phone number",
              minLength: { value: 10, message: "Phone number is invalid" },
            }}
            render={({ field, fieldState }) => {
              const MAX_DIGITS = 10;

              const handleChange = (e) => {
                let value = e.target.value || "";
                let digits = value.replace(/\D/g, "");
                if (digits.length > MAX_DIGITS) {
                  digits = digits.slice(0, MAX_DIGITS);
                }
                field.onChange(digits);
              };

              const handlePaste = (e) => {
                let pasted = e.clipboardData.getData("Text") || "";
                console.log("pasted", pasted);

                if (pasted.startsWith("+1")) {
                  pasted = pasted.slice(2);
                }

                pasted = pasted.replace(/\D/g, "");
                if (pasted.length > MAX_DIGITS) {
                  pasted = pasted.slice(-MAX_DIGITS);
                }

                e.preventDefault();
                field.onChange(`${pasted}`);
              };

              return (
                <div className="field">
                  <label className={styles.label}>Phone Number *</label>
                  <div className={styles.inputWrapper}>
                    <div className="p-inputgroup flex-1">
                      <span
                        className={`p-inputgroup-addon ${styles.phonePrefix}`}
                      >
                        +1
                      </span>
                      <InputText
                        {...field}
                        value={field.value || ""}
                        onChange={handleChange}
                        onPaste={handlePaste}
                        className={`w-full ${styles.input} ${
                          styles.phoneInput
                        } ${fieldState.invalid ? "p-invalid" : ""}`}
                        placeholder="(999) 999-9999"
                      />
                    </div>

                    {fieldState.error && (
                      <small className="p-error">
                        {fieldState.error.message}
                      </small>
                    )}

                    <p className={styles.phoneNote}>
                      <b>⚠️ NOTE!</b> if you choose <b>“Text message”</b>, you
                      are <b>opting in and giving consent</b> to receive text
                      messages regarding your order and when your order is ready
                      for pickup. <b>Msg & Data Rates May Apply.⚠️ </b>
                    </p>
                  </div>
                </div>
              );
            }}
          />
        );

      default:
        return null;
    }
  };

  return (
    <>
      {currentStation ? (
        <div className={styles.pageWrapper}>
          <>
            <div className={styles.orderCard}>
              <div
                style={{
                  backgroundImage: headerImageUrl
                    ? `url(${process.env.BACKEND_URL.replace("/api", "")}/public/images/${headerImageUrl.split("/").pop()})`
                    : "",
                }}
                className={styles.stationImage}
              >
                <Link to="/" className={styles.backButton}>
                  <i className={`pi pi-angle-left `}></i>
                </Link>

                <div className={styles.estimatedTime}>
                  {currentStation?.statistics?.estimated_time
                    ? `~ ${currentStation?.statistics.estimated_time} wait`
                    : `~ ${formatOrderRestriction(
                        currentStation?.baseline_time || "00:06",
                      )} wait`}
                </div>
              </div>
              <div className={styles.formWrapper}>
                {!isCreateOrderSuccess ? (
                  <>
                    {currentStation?.order_restriction &&
                      currentStation?.order_restriction !== "00:00" && (
                        <p className={styles.orderRestrictions}>
                          You can make one order per every{" "}
                          {formatOrderRestriction(
                            currentStation?.order_restriction,
                          )}
                          .
                        </p>
                      )}
                    <h1>{`CREATE ${currentStation?.name || ""} ORDER`}</h1>
                    <div
                      className={classNames(styles.stationInfo, "stationInfo")}
                    >
                      <LinkView html={currentStation?.info || ""} />
                    </div>
                    <form onSubmit={handleSubmit(onCreate)}>
                      <div className="field">
                        <label className={styles.label}>Name *</label>

                        <div className={styles.nameWrapper}>
                          <Controller
                            name="name"
                            control={control}
                            rules={{
                              required: "Enter your first name",
                              maxLength: {
                                value: 256,
                                message:
                                  "Name should be shorter than 256 characters",
                              },
                            }}
                            render={({ field, fieldState }) => (
                              <div className={styles.inputWrapper}>
                                <InputText
                                  className={`w-full ${styles.input} ${
                                    fieldState.invalid ? "p-invalid" : ""
                                  }`}
                                  placeholder="Enter Your First Name"
                                  {...field}
                                />
                                {fieldState.error && (
                                  <small className={`p-error `}>
                                    {fieldState.error.message}
                                  </small>
                                )}
                              </div>
                            )}
                          />

                          <Controller
                            name="surname"
                            control={control}
                            rules={{
                              required: "Enter your last name",
                              maxLength: {
                                value: 256,
                                message:
                                  "Last Name should be shorter than 256 characters",
                              },
                            }}
                            render={({ field, fieldState }) => (
                              <div className={styles.inputWrapper}>
                                <InputText
                                  className={`w-full ${styles.input} ${
                                    fieldState.invalid ? "p-invalid" : ""
                                  }`}
                                  placeholder="Enter Your Last Name"
                                  {...field}
                                />
                                {fieldState.error && (
                                  <small className={`p-error `}>
                                    {fieldState.error.message}
                                  </small>
                                )}
                              </div>
                            )}
                          />
                        </div>
                      </div>

                      {isGetDiningOptionsSuccess && (
                        <Controller
                          name="dining_option"
                          control={control}
                          rules={{ required: "Choose Dining Option" }}
                          render={({ field, fieldState }) => (
                            <div className="field">
                              <label className={styles.label}>
                                Please Choose Dining Option *
                              </label>
                              <div className={styles.dropDownWrapper}>
                                <Dropdown
                                  {...field}
                                  className={`${styles.withOutImage} ${
                                    fieldState.invalid ? "p-invalid" : ""
                                  }`}
                                  placeholder="Select a dining option"
                                  options={options}
                                  optionLabel="name"
                                  optionValue="id"
                                  panelClassName={`${styles.withOutImage} ${styles.dropDownPanel}`}
                                />
                                {fieldState.error && (
                                  <small className={`p-error `}>
                                    {fieldState.error.message}
                                  </small>
                                )}
                              </div>
                            </div>
                          )}
                        />
                      )}

                      {isGetNotificationsSuccess && (
                        <Controller
                          name="notification"
                          control={control}
                          defaultValue={singleNotificationType ?? undefined}
                          rules={{ required: "Choose Notification Option" }}
                          render={({ field, fieldState }) => {
                            return (
                              <div className="field">
                                <label className={styles.label}>
                                  Notification option *
                                </label>
                                <div
                                  className={classNames(
                                    styles.dropDownWrapper,
                                    styles.notificationDropdown,
                                    field.value && field.value !== "push"
                                      ? styles.notificationDropdownSelected
                                      : "",
                                  )}
                                >
                                  <Dropdown
                                    {...field}
                                    placeholder="Select an option"
                                    optionLabel="label"
                                    optionValue="value"
                                    disabled={!!singleNotificationType}
                                    className={`${styles.withOutImage} ${
                                      fieldState.invalid ? "p-invalid" : ""
                                    }`}
                                    options={(filteredNotifications || [])
                                      .map((data) => {
                                        return {
                                          value: data.title,
                                          label:
                                            data.title
                                              ?.slice(0, 1)
                                              .toUpperCase() +
                                            data.title?.slice(1),
                                        };
                                      })}
                                    panelClassName={`${styles.withOutImage} ${styles.dropDownPanel}`}
                                  />

                                  {showMobilePushInfo &&
                                    !isRunningAsPWA() &&
                                    (isIOS() ? (
                                      <div
                                        className={styles.pushMobileInfoText}
                                      >
                                        In order to use Push notifications in
                                        mobile - please, add website to Home
                                        Screen
                                      </div>
                                    ) : (
                                      <>
                                        {hasInstalledPwa ? (
                                          <div
                                            className={
                                              styles.pushMobileInfoText
                                            }
                                          >
                                            In order to use Push notifications
                                            in mobile - please,
                                            <span
                                              onClick={() => {
                                                window.open(
                                                  "https://tu.dwtl.food",
                                                  "_blank",
                                                );
                                              }}
                                              className={styles.installLink}
                                            >
                                              use installed version
                                            </span>
                                          </div>
                                        ) : (
                                          <div
                                            className={
                                              styles.pushMobileInfoText
                                            }
                                          >
                                            In order to use Push notifications
                                            in mobile - please,
                                            <span
                                              onClick={installPWA}
                                              className={styles.installLink}
                                            >
                                              Add to Home Screen
                                            </span>
                                          </div>
                                        )}
                                      </>
                                    ))}
                                  {fieldState.error && (
                                    <small className={`p-error `}>
                                      {fieldState.error.message}
                                    </small>
                                  )}
                                </div>
                              </div>
                            );
                          }}
                        />
                      )}

                      {contactFieldsRenderer()}

                      <Controller
                        name="allergies"
                        control={control}
                        rules={{ required: "Choose Allergies" }}
                        render={({ field, fieldState }) => (
                          <div className="field">
                            <label className={styles.label}>Allergies *</label>
                            {isMobile ? (
                              <MobileSelectModal
                                buttonText="Choose Allergies"
                                value={field.value}
                                onChange={field.onChange}
                                options={allergiesOptions}
                                header="Choose Allergies *"
                                allowMultiple
                                max={Infinity}
                                valueKey="value"
                                labelKey="label"
                                imageResolver={(opt) =>
                                  allergiesImageMapper[opt.value]
                                }
                                labelMapper={allergiesNameMapper}
                              />
                            ) : (
                              <div
                                className={`${styles.multiSelectWrapper} ${styles.allergiesWrapper}`}
                              >
                                <MultiSelect
                                  {...field}
                                  itemTemplate={(item) => {
                                    return (
                                      <div className={styles.listItem}>
                                        <div
                                          style={{
                                            backgroundImage: `url(${
                                              allergiesImageMapper[item.value]
                                            })`,
                                          }}
                                        ></div>
                                        {item.label}
                                      </div>
                                    );
                                  }}
                                  onChange={(e) => {
                                    if (
                                      field?.value?.length === 1 &&
                                      field?.value[0] === "none" &&
                                      e.selectedOption.value !== "none" &&
                                      e.selectedOption.value !== undefined
                                    ) {
                                      return field.onChange([
                                        e.selectedOption.value,
                                      ]);
                                    }

                                    if (e.selectedOption.value === "none") {
                                      return field.onChange([
                                        e.selectedOption.value,
                                      ]);
                                    }

                                    return field.onChange(e);
                                  }}
                                  options={allergiesOptions}
                                  optionLabel="label"
                                  optionValue="value"
                                  display="chip"
                                  placeholder="Select an allergies"
                                  className={`${styles.multiSelect} ${
                                    fieldState.invalid ? "p-invalid" : ""
                                  }`}
                                  panelClassName={`${styles.multiSelectPanel}`}
                                />
                                {fieldState.error && (
                                  <small className={`p-error `}>
                                    {fieldState.error.message}
                                  </small>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      />

                      {(() => {
                        const hasRealAllergens =
                          formValues.allergies &&
                          formValues.allergies.length > 0 &&
                          !formValues.allergies.every((a) => a === "none");
                        return hasRealAllergens ? (
                          <div className={styles.allergenToggleRow}>
                            <Checkbox
                              inputId="show-allergen-items"
                              checked={showAllergenItems}
                              onChange={(e) => setShowAllergenItems(e.checked)}
                              className={styles.allergenCheckbox}
                            />
                            <label
                              htmlFor="show-allergen-items"
                              className={styles.allergenToggleLabel}
                            >
                              Show menu items with my allergens
                            </label>
                          </div>
                        ) : null;
                      })()}

                      {isGetStationOptionsSuccess &&
                        (filteredGroups || stationOptions || []).map(
                          (rawOption) => {
                            const option = {
                              ...rawOption,
                              items: (rawOption.items || []).filter(
                                (item) => item.visible,
                              ),
                            };

                            if (!option.items || option.items.length === 0) {
                              return null;
                            }

                            return (
                              <Controller
                                key={option.group.group_name}
                                name={`menu_option-${option.group.group_name}`}
                                control={control}
                                rules={{
                                  required: `Choose ${option.group.group_name}`,
                                  validate: (e) => {
                                    if (
                                      option.group.allow_multiple &&
                                      e?.length > option.group.max_multiple
                                    ) {
                                      return `You can select only ${option.group.max_multiple} options`;
                                    }
                                  },
                                }}
                                render={({ field, fieldState }) => (
                                  <div className="field">
                                    <label className={styles.label}>
                                      {option.group.group_name} *{" "}
                                      {!!option.group.allow_multiple && (
                                        <span
                                          className={styles.maxOptionsLabel}
                                        >
                                          (max {option.group.max_multiple})
                                        </span>
                                      )}
                                    </label>

                                    {isMobile ? (
                                      <MobileSelectModal
                                        buttonText={`Choose ${option.group.group_name}`}
                                        value={field.value || []}
                                        onChange={field.onChange}
                                        options={option.items}
                                        header={`Choose ${option.group.group_name}`}
                                        allowMultiple={
                                          option.group.allow_multiple
                                        }
                                        max={option.group.max_multiple}
                                        disabled={option.items.length === 1}
                                        valueKey="id"
                                        labelKey="name"
                                        imageResolver={(item) =>
                                          item?.image_name
                                            ? getImageUrl(item.image_name)
                                            : NoImg
                                        }
                                        labelMapper={option.items.reduce(
                                          (acc, item) => {
                                            acc[item.id] = item.name;
                                            return acc;
                                          },
                                          {},
                                        )}
                                        infoIdResolver={(item) => item?.id}
                                      />
                                    ) : option.group.allow_multiple ? (
                                      <div
                                        className={styles.multiSelectWrapper}
                                      >
                                        <MultiSelect
                                          {...field}
                                          options={option.items}
                                          optionLabel="name"
                                          optionValue="id"
                                          display="chip"
                                          placeholder={`Select ${option.group.group_name}`}
                                          disabled={option.items.length === 1}
                                          className={`${styles.multiSelect} ${
                                            fieldState.invalid
                                              ? "p-invalid"
                                              : ""
                                          }`}
                                          panelClassName={
                                            styles.multiSelectPanel
                                          }
                                          itemTemplate={(item) => (
                                            <div className={styles.listItem}>
                                              <div
                                                style={{
                                                  background: `url(${process.env.BACKEND_URL.replace(
                                                    "/api",
                                                    "",
                                                  )}/public/images/${
                                                    item?.image_name
                                                  }), url(${NoImg})`,
                                                  backgroundSize:
                                                    "cover, cover",
                                                }}
                                              />
                                              <span
                                                className={styles.listItemLabel}
                                              >
                                                {item.name}
                                              </span>
                                              <MenuItemInfoIcon
                                                optionId={item.id}
                                                itemName={item.name}
                                              />
                                            </div>
                                          )}
                                        />
                                        {fieldState.error && (
                                          <small className="p-error">
                                            {fieldState.error.message}
                                          </small>
                                        )}
                                      </div>
                                    ) : (
                                      <div className={styles.dropDownWrapper}>
                                        <Dropdown
                                          {...field}
                                          options={option.items}
                                          optionLabel="name"
                                          optionValue="id"
                                          placeholder={`Select ${option.group.group_name}`}
                                          panelClassName={styles.dropDownPanel}
                                          disabled={option.items.length === 1}
                                          className={
                                            fieldState.invalid
                                              ? "p-invalid"
                                              : ""
                                          }
                                          itemTemplate={(item) => (
                                            <div className={styles.listItem}>
                                              <div
                                                style={{
                                                  background: `url(${process.env.BACKEND_URL.replace(
                                                    "/api",
                                                    "",
                                                  )}/public/images/${
                                                    item?.image_name
                                                  }), url(${NoImg})`,
                                                  backgroundSize:
                                                    "cover, cover",
                                                }}
                                              />
                                              <span
                                                className={styles.listItemLabel}
                                              >
                                                {item.name}
                                              </span>
                                              <MenuItemInfoIcon
                                                optionId={item.id}
                                                itemName={item.name}
                                              />
                                            </div>
                                          )}
                                        />
                                        {fieldState.error && (
                                          <small className="p-error">
                                            {fieldState.error.message}
                                          </small>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                              />
                            );
                          },
                        )}

                      <Controller
                        name="notes"
                        control={control}
                        rules={{
                          maxLength: {
                            value: 100,
                            message: "Maximum 100 characters",
                          },
                        }}
                        render={({ field, fieldState }) => (
                          <div className="field">
                            <label className={styles.label}>
                              Special Notes
                            </label>
                            <InputTextarea
                              {...field}
                              value={field.value || ""}
                              onChange={(e) => field.onChange(e.target.value)}
                              onBlur={field.onBlur}
                              className={`w-full ${styles.textArea} ${
                                fieldState.invalid && "p-invalid"
                              }`}
                            />
                            {fieldState.error && (
                              <p className={styles.error}>
                                Notes limit is 100 characters
                              </p>
                            )}
                          </div>
                        )}
                      />

                      <Button
                        id="submitButton"
                        label="Submit Order"
                        type="submit"
                        disabled={isOrderLoading}
                        className={styles.submitButton}
                      />
                    </form>
                  </>
                ) : (
                  <>
                    <div className={styles.orderCreated}>
                      Order confirmed, {getValues("name")}{" "}
                      {getValues("surname")}! The DWTL Culinary Team is firing
                      up the magic — we’ll notify you when your creation is
                      ready!
                    </div>
                    <Confetti />
                  </>
                )}
              </div>
            </div>
          </>
        </div>
      ) : (
        <div style={{ textAlign: "center", fontSize: "36px" }}>
          Station is closed
        </div>
      )}
    </>
  );
};

export default OrderPage;
